/**
 * This file contains validators for AI responses to ensure
 * they contain supported actions and structured response types.
 */
import { AIResponse } from '../types/server-types';
import { ACTION_TYPES, ActionType } from '../types/server-types/actions';
import {
    RESPONSE_TYPES,
    StructuredResponseType
} from '../types/server-types/structured-responses';

/**
 * Validates if an action type is supported
 */
export function isValidActionType(type: string): type is ActionType {
    return Object.values(ACTION_TYPES).includes(type as ActionType);
}

/**
 * Validates if a structured response type is supported
 */
export function isValidResponseType(
    type: string
): type is StructuredResponseType {
    return Object.values(RESPONSE_TYPES).includes(
        type as StructuredResponseType
    );
}

/**
 * Get the action type from a response if it's an action response
 * @returns The action type string or null if not an action response
 */
export function getActionType(response: AIResponse): string | null {
    if (
        response.type === 'action' &&
        response.content &&
        typeof response.content === 'object'
    ) {
        const actionContent = response.content as { type: string };
        return actionContent.type;
    }
    return null;
}

/**
 * Get the structured response type from a response if it's a structured response
 * @returns The structured response type string or null if not a structured response
 */
export function getStructuredResponseType(response: AIResponse): string | null {
    if (
        response.type === 'structured' &&
        response.content &&
        typeof response.content === 'object'
    ) {
        const structuredContent = response.content as { type: string };
        return structuredContent.type;
    }
    return null;
}

/**
 * Checks if an AI response has any unsupported types
 * Returns the unsupported data value if not supported, or null if supported
 */
export function getUnsupportedTypeData(response: AIResponse): string | null {
    // Check action type
    const actionType = getActionType(response);
    if (actionType !== null && !isValidActionType(actionType)) {
        return actionType;
    }

    // Check structured response type
    const structuredType = getStructuredResponseType(response);
    if (structuredType !== null && !isValidResponseType(structuredType)) {
        return structuredType;
    }

    // No unsupported types found
    return null;
}
